import React, { useEffect, useMemo, useRef, useState } from 'react'
import { customDropdownStyles } from "../../data/data";
import FormCard from './FormCard';
import Select from "react-select";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useApiServiceForecast } from '../../services/apiServiceForecast';
import toast from 'react-hot-toast';
import { formValidationSchemaModels } from '../../schema/formValidationSchema';
import { formatDate } from '../../utils/utils';
import Loading from '../Common/Loading';
import TimeSeriesChart from '../../charts/timeseriesChart';
import LiveForecastsTable from '../Common/LiveForecastsTable';
import ForecastChart from '../../charts/ForecastChart';
import ConfirmationPopup from '../Common/ConfirmationPopup';
import AnomaliesTable from "../Common/AnomaliesTable.jsx";

const ForecastForm = () => {
    const [isForecastSelection, setIsForecastSelection] = useState(false);
    const [isBusinessRules, setIsBusinessRules] = useState(false);
    const [frequency, setFrequency] = useState(30);
    const [loading, setLoading] = useState(true);
    const [selectedAlias, setSelectedAlias] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [businessRuleValue, setBusinessRuleValue] = useState(0);
    const [businessRuleName, setBusinessRuleName] = useState("");
    const [businessRules, setBusinessRules] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [editAlias, setEditAlias] = useState(null);
    const [editOperator, setEditOperator] = useState(null);
    const [editValue, setEditValue] = useState(0);
    const [editName, setEditName] = useState("");
    const [chartLoading, setChartLoading] = useState(false);
    const [forecastChartLoading, setForecastChartLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [forecastTimeseriesData, setForecastTimeseriesData] = useState(null);
    const [forecastTagAlias, setForecastTagAlias] = useState([]);
    const [forecastChartData, setForecastChartData] = useState(null);
    const [liveForecastsData, setLiveForecastsData] = useState([]);
    const [selectedModelName, setSelectedModelName] = useState(null);
    const [showForecastChart, setShowForecastChart] = useState(false);
    const [isNoForecastDataPopup, setIsNoForecastDataPopup] = useState(false);
    const [isNoExistingModelsPopup, setIsNoExistingModelsPopup] = useState(false);
    const [isNoModelsFoundPopup, setIsNoModelsFoundPopup] = useState(false);
    const [selectedAnomaly, setSelectedAnomaly] = useState(null);
    const skipFetchRef = useRef(false);

    const [dropdownOptions, setDropdownOptions] = useState({
        assemblyOptions: [],
        dryGasSealTagsOptions: [],
        independentTagsOptions: [],
    });

    const [selectedValues, setSelectedValues] = useState({
        selectedAssembly: null,
        selectedDryGasSealTags: null,
        selectedIndependentTags: null,
    });

    const [nameDesc, setNameDesc] = useState({
        name: "",
        desc: "",
    });
    const selectedTagOrder = useMemo(() => {
        const dgsAliases = selectedValues.selectedDryGasSealTags?.tagAliasList
            ? JSON.parse(selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"'))
            : [];
        const independentAliases = Array.isArray(selectedValues.selectedIndependentTags)
            ? selectedValues.selectedIndependentTags.map((option) => option.tagAlias).filter(Boolean)
            : [];

        return [...dgsAliases, ...independentAliases];
    }, [selectedValues.selectedDryGasSealTags, selectedValues.selectedIndependentTags]);

    const forecastTagOrder = useMemo(
        () => forecastChartData?.tag_aliases ?? selectedTagOrder,
        [forecastChartData, selectedTagOrder]
    );


    // Calculate default dates
    const today = new Date();
    const trainingEndDate = new Date(today);
    trainingEndDate.setDate(trainingEndDate.getDate() - 1); // 1 day before today

    const trainingStartDate = new Date(today);
    trainingStartDate.setMonth(trainingStartDate.getMonth() - 6); // 6 months before today

    const futureStartDate = new Date(today);
    futureStartDate.setDate(futureStartDate.getDate() - 1); // 1 day before today (yesterday)

    const futureEndDate = new Date(futureStartDate);
    futureEndDate.setMonth(futureEndDate.getMonth() + 3); // 3 months from start date

    const [trainingStartDateState, setTrainingStartDate] = useState(formatDate(trainingStartDate));
    const [trainingEndDateState, setTrainingEndDate] = useState(formatDate(trainingEndDate));

    const [futureStartDateState, setFutureStartDate] = useState(formatDate(futureStartDate));
    const [futureEndDateState, setFutureEndDate] = useState(formatDate(futureEndDate));

    const toggleForecastSelection = () => {
        setIsForecastSelection((prev) => !prev);
    };

    const toggleBusinessRule = () => {
        setIsBusinessRules((prev) => !prev);
    };

    const {
        getAssemblies,
        getDryGasSealTags,
        getIndependentTags,
        getTimeSeriesChartData,
        startForecast,
        getForecastData,
        getForecastModelList,
        deleteForecastModel,
        updateForecastModelStatus
    } = useApiServiceForecast();

    const fetchDropdownData = async (
        dropdownName,
        apiCall,
        mapFunction,
        datasource = null
    ) => {
        try {
            const data = await apiCall(datasource);
            if (Array.isArray(data)) {
                const options = data.map(mapFunction);
                const uniqueOptions = Array.from(
                    new Set(options.map((option) => option.value))
                ).map((value) => options.find((option) => option.value === value));
                setDropdownOptions((prev) => ({
                    ...prev,
                    [`${dropdownName}Options`]: uniqueOptions,
                }));

            }
        } catch (error) {
            toast.error(
                `Error fetching ${dropdownName} data. Please try again later.`
            );
            console.error(
                `Error fetching ${dropdownName} data in Models Screen:`,
                error
            );
            return null;
        }
    };

    const mapAssembly = (item) => ({
        label: item.DRIVER + " " + item.PROJECTNAME,
        value: item.DRIVER + " " + item.PROJECTNAME,
        datasource: item.DATASOURCE,
    });
    const mapDryGasSealTags = (item) => {
        const depTagAliasArray = item.TAG_ALIAS_LIST.replace(/[\[\]']/g, "").split(
            ", "
        );
        const formattedString = depTagAliasArray.map((tag) => `${tag}`).join(",");
        return {
            label: formattedString,
            value: formattedString,
            tagName: item.TAG_NAME,
            modelId: item.MODEL_ID,
            datasource: item.DATASOURCE_ID,
            tagAliasList: item.TAG_ALIAS_LIST,
        };
    };

    const mapIndependentTags = (item) => ({
        label: item.TAG_NAME,
        value: item.TAG_NAME,
        datasource: item.DATASOURCE,
        tagAlias: item.INDEP_TAG_ALIAS,
    });

    const getDgsTagAliases = (dryGasTag) => {
        if (!dryGasTag?.tagAliasList) {
            return [];
        }

        try {
            return JSON.parse(dryGasTag.tagAliasList.replace(/'/g, '"'));
        } catch (error) {
            console.error("Error parsing DGS tag aliases:", error);
            return [];
        }
    };

    const getIndependentTagAliases = (independentTags) =>
        Array.isArray(independentTags)
            ? independentTags.map((option) => option.tagAlias).filter(Boolean)
            : [];

    const getCombinedTagAliases = (dryGasTag, independentTags) => [
        ...getDgsTagAliases(dryGasTag),
        ...getIndependentTagAliases(independentTags),
    ];

    const getDatasource = (assembly, dryGasTag, independentTags) =>
        dryGasTag?.datasource ||
        assembly?.datasource ||
        (Array.isArray(independentTags) && independentTags.length > 0
            ? independentTags[0]?.datasource
            : null);


    useEffect(() => {
        const fetchData = async () => {
            await fetchDropdownData("assembly", getAssemblies, mapAssembly);
            setLoading(false);
        };
        fetchData();
    }, []);


    // Fetch chart data when frequency, startDateState, endDateState changes
    useEffect(() => {
        const fetchChartData = async () => {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );

            if (
                selectedValues.selectedAssembly &&
                datasource &&
                combinedTagAliases.length > 0
            ) {
                // Prepare business_rules array for API
                const business_rules = businessRules.map((rule) => ({
                    ALIAS: rule.alias?.value || rule.alias,
                    OPERATOR: rule.operator?.value || rule.operator,
                    VALUE: rule.value,
                }));

                setChartLoading(true);
                try {
                    const data = await getTimeSeriesChartData(
                        trainingStartDateState,
                        trainingEndDateState,
                        datasource,
                        combinedTagAliases,
                        frequency,
                        business_rules
                    );

                    setForecastTimeseriesData(data);
                } catch (error) {
                    setForecastTimeseriesData(null); // Clear data on error to prevent rendering with invalid data
                    setChartLoading(false);
                    toast.error(
                        "Error fetching time series chart data. Please try again."
                    );
                    console.error("Error fetching time series chart data:", error);
                } finally {
                    setChartLoading(false);
                }
            }
        };

        fetchChartData();
    }, [
        frequency,
        trainingStartDateState,
        trainingEndDateState,
        selectedValues.selectedDryGasSealTags,
        selectedValues.selectedIndependentTags,
    ]);

    // Auto-select all tag aliases by default when Dry Gas Seal Tags or Independent Tags are selected
    useEffect(() => {
        const allTagOptions = [];

        // Add DGS tag aliases
        if (selectedValues.selectedDryGasSealTags?.tagAliasList) {
            const tagAliases = JSON.parse(
                selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"')
            );
            const dgsTagOptions = tagAliases.map((tag) => ({
                label: tag,
                value: tag,
            }));
            allTagOptions.push(...dgsTagOptions);
        }

        // Add independent tag aliases
        if (selectedValues.selectedIndependentTags && Array.isArray(selectedValues.selectedIndependentTags)) {
            const independentTagOptions = selectedValues.selectedIndependentTags.map((option) => ({
                label: option.tagAlias,
                value: option.tagAlias,
            }));
            allTagOptions.push(...independentTagOptions);
        }

        setForecastTagAlias(allTagOptions);
    }, [selectedValues.selectedDryGasSealTags, selectedValues.selectedIndependentTags]);

    // Reload forecast chart data when future dates change
    useEffect(() => {
        // Skip fetch if it was just called from handleViewForecastFromTable
        if (skipFetchRef.current) {
            skipFetchRef.current = false;
            return;
        }

        const fetchForecastChartData = async () => {
            if (selectedModelName) {
                setForecastChartLoading(true);
                try {
                    const data = await getForecastData(
                        futureStartDateState,
                        futureEndDateState,
                        selectedModelName
                    );
                    setForecastChartData(data);
                } catch (error) {
                    console.error("Error fetching forecast data:", error);
                    toast.error("Failed to fetch forecast data");
                } finally {
                    setForecastChartLoading(false);
                }
            }
        };

        fetchForecastChartData();
    }, [futureStartDateState, futureEndDateState]);

    // Auto-reload forecast models while any model is in progress
    useEffect(() => {
        const interval = setInterval(() => {
            if (liveForecastsData.some(model => model.TRAINING_STATUS === 'RUNNING' || model.TRAINING_STATUS === 'INPROGRESS')) {
                reloadForecastModels();
            }
        }, 15000); // Check every 15 seconds

        // Clear the interval on component unmount
        return () => clearInterval(interval);
    }, [liveForecastsData]);

    const validateField = (fieldName, value) => {
        formValidationSchemaModels
            .validateAt(fieldName, { [fieldName]: value })
            .then(() => {
                setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: null }));
            })
            .catch((validationError) => {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [fieldName]: validationError.message,
                }));
            });
    };


    const handleSelectChange = async (name, selectedOption) => {

        const dataSourceForDgsTags =
            name === "selectedAssembly"
                ? selectedOption?.datasource
                : selectedValues.selectedAssembly?.datasource;


        if (name === "selectedAssembly") {
            setSelectedValues((prev) => ({
                ...prev,
                selectedAssembly: selectedOption,
                selectedDryGasSealTags: null,
                selectedIndependentTags: null,

            }));
            // Hide the forecast chart when assembly changes
            setShowForecastChart(false);
            setForecastChartData(null);
            // Clear the timeseries chart data to prevent rendering with stale data
            setForecastTimeseriesData(null);
            setLiveForecastsData([]);
        } else if (name === "selectedDryGasSealTags") {
            setSelectedValues((prev) => ({
                ...prev,
                selectedDryGasSealTags: selectedOption,

            }));
            // Hide the forecast chart when DGS tags change
            // Clear the timeseries chart data to prevent rendering with stale data
            setForecastTimeseriesData(null);

        } else if (name === "selectedIndependentTags") {
            setSelectedValues((prev) => ({
                ...prev,
                selectedIndependentTags: selectedOption,

            }));
            // Hide the forecast chart when Independent tags change
            setShowForecastChart(false);
            setForecastChartData(null);
            // Clear the timeseries chart data to prevent rendering with stale data
            setForecastTimeseriesData(null);
        } else {
            setSelectedValues((prev) => ({ ...prev, [name]: selectedOption }));
        }

        const fieldsToValidate = ["selectedAssembly", "selectedDryGasSealTags"];
        if (fieldsToValidate.includes(name)) {
            validateField(name, selectedOption);
        }

        if (name === "selectedAssembly" && selectedOption) {
            const datasource = selectedOption.datasource;
            setLoading(true);
            try {
                await Promise.all([
                    fetchDropdownData(
                        "dryGasSealTags",
                        getDryGasSealTags,
                        mapDryGasSealTags,
                        datasource
                    ),
                    fetchDropdownData(
                        "independentTags",
                        getIndependentTags,
                        mapIndependentTags,
                        datasource
                    )
                ]);
            } catch (e) {
                toast.error(
                    "Error fetching tags. Please try again later.",
                    e
                );
                setLoading(false);
            } finally {
                setLoading(false);
            }
        }

        if (name === "selectedDryGasSealTags" || name === "selectedIndependentTags") {
            const nextDryGasTag =
                name === "selectedDryGasSealTags"
                    ? selectedOption
                    : selectedValues.selectedDryGasSealTags;
            const nextIndependentTags =
                name === "selectedIndependentTags"
                    ? selectedOption
                    : selectedValues.selectedIndependentTags;
            const combinedTagAliases = getCombinedTagAliases(nextDryGasTag, nextIndependentTags);
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                nextDryGasTag,
                nextIndependentTags
            );

            if (!selectedValues.selectedAssembly || !datasource || combinedTagAliases.length === 0) {
                setLiveForecastsData([]);
                return;
            }

            setLoading(true);
            try {
                // Fetch live forecasts data
                const forecastsData = await getForecastModelList(
                    datasource,
                    combinedTagAliases
                );

                // Check if forecast list data is empty
                if (!forecastsData || !Array.isArray(forecastsData) || forecastsData.length === 0) {
                    setIsNoModelsFoundPopup(true);
                }

                setLiveForecastsData(forecastsData);
            } catch (error) {
                setLoading(false);
                toast.error(
                    "Error fetching forecast models. Please try again."
                );
                console.error("Error fetching forecast models:", error);
            } finally {
                setLoading(false);
            }
        }

    }


    const isBusinessRuleAddEnabled =
        selectedAlias &&
        selectedOperator &&
        businessRuleValue !== "" &&
        businessRuleName.trim() !== "";

    const handleAddBusinessRule = () => {
        const newRule = {
            alias: selectedAlias,
            operator: selectedOperator,
            value: Number(businessRuleValue),
            name: businessRuleName,
        };
        if (editIndex !== null) {
            // Edit mode
            setBusinessRules((rules) =>
                rules.map((r, i) => (i === editIndex ? newRule : r))
            );
            setEditIndex(null);
        } else {
            // Add mode
            setBusinessRules((rules) => [...rules, newRule]);
        }
        // Clear the input fields after add/update
        setSelectedAlias(null);
        setSelectedOperator(null);
        setBusinessRuleValue(0);
        setBusinessRuleName("");

    };

    const handleDeleteBusinessRule = (idx) => {
        setBusinessRules((rules) => rules.filter((_, i) => i !== idx));
    };

    const handleEditBusinessRule = (idx) => {
        const rule = businessRules[idx];
        setEditAlias(rule.alias);
        setEditOperator(rule.operator);
        setEditValue(rule.value);
        setEditName(rule.name);
        setEditIndex(idx);
    };

    // Add this function inside TrainingForm
    const handleClearAllBusinessRules = async () => {
        setBusinessRules([]);
        setSelectedAlias(null);
        setSelectedOperator(null);
        setBusinessRuleValue(0);
        setBusinessRuleName("");
        setEditIndex(null);

        // Call chart API again with no business rules
        if (
            selectedValues.selectedAssembly
        ) {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );

            if (!datasource || combinedTagAliases.length === 0) {
                return;
            }

            setChartLoading(true);
            setLoading(true);
            try {
                const data = await getTimeSeriesChartData(
                    trainingStartDateState,
                    trainingEndDateState,
                    datasource,
                    combinedTagAliases,
                    frequency,
                    [] // No business rules
                );
                setForecastTimeseriesData(data);
            } catch (error) {
                setForecastTimeseriesData(null); // Clear data on error
                setLoading(false);
                setChartLoading(false);
                console.error("Error while clearing business rules:", error);
            } finally {
                setLoading(false);
                setChartLoading(false);
            }
        }
    };

    // Add this function inside TrainingForm
    const handleSaveEditBusinessRule = () => {
        const updatedRule = {
            alias: editAlias,
            operator: editOperator,
            value: Number(editValue),
            name: editName,
        };
        setBusinessRules((rules) =>
            rules.map((r, i) => (i === editIndex ? updatedRule : r))
        );
        setEditIndex(null);
        setEditAlias(null);
        setEditOperator(null);
        setEditValue("");
        setEditName("");
    };

    const handleApplyBusinessRules = async () => {
        if (
            selectedValues.selectedAssembly
        ) {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );

            if (!datasource || combinedTagAliases.length === 0) {
                return;
            }

            const business_rules = businessRules.map((rule) => ({
                ALIAS: rule.alias?.value || rule.alias,
                OPERATOR: rule.operator?.value || rule.operator,
                VALUE: rule.value,
            }));

            setChartLoading(true);
            setLoading(true);
            try {
                const data = await getTimeSeriesChartData(
                    trainingStartDateState,
                    trainingEndDateState,
                    datasource,
                    combinedTagAliases,
                    frequency,
                    business_rules
                );
                setForecastTimeseriesData(data);
            } catch (error) {
                setForecastTimeseriesData(null); // Clear data on error
                toast.error("Error applying business rules. Please try again.");
                setLoading(false);
                setChartLoading(false);
                console.error("Error applying business rules:", error);
            } finally {
                setLoading(false);
                setChartLoading(false);
            }
        }
    };

    const clearFormData = () => {
        // Clear form data after forecast starts (keep tags selected)
        setBusinessRules([]);
        setErrors({});
        setNameDesc({ name: "", desc: "" });
        setForecastChartData(null);
        setShowForecastChart(false);
    };

    const handleForecast = async () => {
        const combinedTagAliases = getCombinedTagAliases(
            selectedValues.selectedDryGasSealTags,
            selectedValues.selectedIndependentTags
        );
        const datasource = getDatasource(
            selectedValues.selectedAssembly,
            selectedValues.selectedDryGasSealTags,
            selectedValues.selectedIndependentTags
        );

        if (!datasource || combinedTagAliases.length === 0) {
            toast.error("Please select at least one DGS tag or independent tag.");
            return;
        }

        const tagName = combinedTagAliases;

        const business_rules = businessRules.map((rule) => ({
            ALIAS: rule.alias?.value || rule.alias,
            OPERATOR: rule.operator?.value || rule.operator,
            VALUE: rule.value,
        }));

        setLoading(true);
        try {
            const response = await startForecast({
                tagAlias: combinedTagAliases,
                tagName,
                datasource_id: datasource,
                frequency,
                startDate: trainingStartDateState,
                endDate: trainingEndDateState,
                forecastStartDate: futureStartDateState,
                forecastEndDate: futureEndDateState,
                name: nameDesc.name,
                desc: nameDesc.desc,
                businessRules: business_rules ?? [],
            });

            if (response && response.forecast_id) {
                toast.success("Forecast started successfully!");
            }

            clearFormData();
            await reloadForecastModels();
        } catch (error) {
            console.error("Error while handling startForecast:", error);
            toast.error("Failed to start forecast");
        } finally {
            setLoading(false);
        }
    };

    const handleViewForecastFromTable = async (row) => {
        setLoading(true);
        // Set flag to skip the useEffect fetch since we're fetching here
        skipFetchRef.current = true;
        try {
            // Update future start and end dates from the clicked model row
            if (row.FUT_ST_DATE) {
                setFutureStartDate(row.FUT_ST_DATE);
            }
            if (row.FUT_END_DATE) {
                setFutureEndDate(row.FUT_END_DATE);
            }

            const data = await getForecastData(
                row.FUT_ST_DATE || futureStartDateState,
                row.FUT_END_DATE || futureEndDateState,
                row.FORECAST_ID
            );

            // Check if data is empty or has no tag_aliases array
            if (!data || !data.tag_aliases || !Array.isArray(data.tag_aliases) || data.tag_aliases.length === 0) {
                setIsNoForecastDataPopup(true);
                setShowForecastChart(false);
                setForecastChartData(null);
                return;
            }

            // Set the forecast chart data for ForecastChart component
            setForecastChartData(data);
            // Store the selected model ID to track which model is currently being viewed
            setSelectedModelName(row.FORECAST_ID);
            // Show the forecast chart
            setShowForecastChart(true);

        } catch (error) {
            console.error("Error fetching forecast data:", error);
            toast.error("Failed to fetch forecast data");
        } finally {
            setLoading(false);
        }
    };

    const reloadForecastModels = async () => {
        if (selectedValues.selectedAssembly) {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );

            if (!datasource || combinedTagAliases.length === 0) {
                setLiveForecastsData([]);
                return;
            }

            setLoading(true);
            try {
                const forecastsData = await getForecastModelList(
                    datasource,
                    combinedTagAliases
                );

                // Check if forecast list data is empty
                if (!forecastsData || !Array.isArray(forecastsData) || forecastsData.length === 0) {
                    setIsNoExistingModelsPopup(true);
                }

                setLiveForecastsData(forecastsData);
            } catch (error) {
                console.error("Error reloading forecast models:", error);
                toast.error("Failed to reload forecast models");
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <>
            <div className="flex justify-center flex-col gap-10 items-center mt-4 mb-20">
                {loading && <Loading />}
                <FormCard>
                    <div>
                        <span className="font-semibold text-2xl text-btn-primary">
                            Forecast
                        </span>
                        <p className="mt-3 text-[14.5px]">
                            Select inference period with ground truth data to see its forecasted values.
                        </p>
                        <form action="" className="mt-8">
                            {/* assembly, dgs tags */}
                            <div className="flex gap-2 flex-col">
                                <div className="flex gap-4">
                                    <div className="flex flex-col w-full">
                                        <label htmlFor="assembly" className="mb-1">
                                            Assembly*
                                        </label>
                                        <Select
                                            id="assembly"
                                            className="w-full"
                                            placeholder="Select Assembly"
                                            options={(dropdownOptions.assemblyOptions ?? []).filter(
                                                (opt) =>
                                                    opt.value !== null &&
                                                    opt.value !== undefined &&
                                                    opt.value !== ""
                                            )}
                                            isClearable
                                            isSearchable
                                            required

                                            styles={customDropdownStyles}
                                            value={selectedValues.selectedAssembly}
                                            onChange={(option) =>
                                                handleSelectChange("selectedAssembly", option)
                                            }
                                        />
                                        {errors.selectedAssembly && (
                                            <p className="text-red-500 text-[14.5px] mt-1">
                                                {errors.selectedAssembly}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label htmlFor="dgs-tags" className="mb-1">
                                            Dry Gas Seal Tags*
                                        </label>
                                        <Select
                                            id="dgs-tags"
                                            placeholder="Select Dry Gas Seal Tags"
                                            isClearable
                                            isSearchable
                                            required
                                            styles={customDropdownStyles}
                                            options={(
                                                dropdownOptions.dryGasSealTagsOptions ?? []
                                            ).filter(
                                                (opt) =>
                                                    opt.value !== null &&
                                                    opt.value !== undefined &&
                                                    opt.value !== ""
                                            )}
                                            value={selectedValues.selectedDryGasSealTags}
                                            onChange={(option) =>
                                                handleSelectChange("selectedDryGasSealTags", option)
                                            }
                                        />
                                        {errors.selectedDryGasSealTags && (
                                            <p className="text-red-500 text-[14.5px] mt-1">
                                                {errors.selectedDryGasSealTags}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label htmlFor="independent-tags" className="mb-1">
                                            Independent Tags
                                        </label>
                                        <Select
                                            id="independent-tags"
                                            placeholder="Select Independent Tags"
                                            isClearable
                                            isSearchable
                                            isMulti
                                            styles={customDropdownStyles}
                                            options={(
                                                dropdownOptions.independentTagsOptions ?? []
                                            ).filter(
                                                (opt) =>
                                                    opt.value !== null &&
                                                    opt.value !== undefined &&
                                                    opt.value !== ""
                                            )}
                                            value={selectedValues.selectedIndependentTags}
                                            onChange={(option) =>
                                                handleSelectChange("selectedIndependentTags", option)
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex flex-col mt-2  w-full">
                                        <label htmlFor="start-date" className="mb-1">
                                            Training Start Date
                                        </label>
                                        <input
                                            type="date"
                                            id="traning-start-date"
                                            className="custom-input"
                                            value={trainingStartDateState}
                                            onChange={(e) => setTrainingStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex flex-col mt-2  w-full">
                                        <label htmlFor="end-date" className="mb-1">
                                            Training End Date
                                        </label>
                                        <input
                                            type="date"
                                            id="training-end-date"
                                            className="custom-input "
                                            value={trainingEndDateState}
                                            onChange={(e) => setTrainingEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5">

                                <div
                                    className="flex justify-between items-center  hover:bg-gray-100 py-3 px-2 hover:cursor-pointer"
                                    onClick={toggleBusinessRule}
                                >
                                    <span className="text-btn-primary font-semibold flex items-center text-[16.5px]">
                                        Business Rules{" "}
                                    </span>
                                    {isBusinessRules ? (
                                        <IoIosArrowDown
                                            size={20}
                                            className="text-btn-primary cursor-pointer"
                                            onClick={toggleBusinessRule}
                                        />
                                    ) : (
                                        <IoIosArrowUp
                                            size={20}
                                            className="text-btn-primary cursor-pointer"
                                            onClick={toggleBusinessRule}
                                        />
                                    )}
                                </div>

                                {


                                    isBusinessRules ? (
                                        <>
                                            <div className="flex gap-4 mb-4 items-center justify-center px-3">
                                                <div className="flex flex-col w-full">
                                                    <label htmlFor="rule-name" className="mb-1">
                                                        Alias{" "}
                                                    </label>
                                                    <Select
                                                        id="rule-name"
                                                        placeholder="Select Alias"
                                                        options={(() => {
                                                            const allAliases = [];

                                                            // Add DGS tag aliases
                                                            if (selectedValues.selectedDryGasSealTags?.tagAliasList) {
                                                                const dgsAliases = JSON.parse(
                                                                    selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"')
                                                                ).map((tag) => ({
                                                                    label: tag,
                                                                    value: tag,
                                                                }));
                                                                allAliases.push(...dgsAliases);
                                                            }

                                                            // Add independent tag aliases
                                                            if (selectedValues.selectedIndependentTags && Array.isArray(selectedValues.selectedIndependentTags)) {
                                                                const independentAliases = selectedValues.selectedIndependentTags.map((option) => ({
                                                                    label: option.tagAlias,
                                                                    value: option.tagAlias,
                                                                }));
                                                                allAliases.push(...independentAliases);
                                                            }

                                                            return allAliases;
                                                        })()}
                                                        value={selectedAlias}
                                                        isClearable
                                                        isSearchable
                                                        styles={customDropdownStyles}
                                                        onChange={setSelectedAlias}
                                                    />
                                                </div>

                                                <div className="flex flex-col w-full">
                                                    <label className="mb-1" htmlFor="operator">
                                                        Operator
                                                    </label>
                                                    <Select
                                                        id="operator"
                                                        placeholder="Select Operator"
                                                        options={[
                                                            { label: "greater", value: "greater" },
                                                            { label: "lower", value: "lower" },
                                                            { label: "equal", value: "equal" },
                                                            { label: "not equal", value: "not equal" },
                                                        ]}
                                                        isClearable
                                                        styles={customDropdownStyles}
                                                        value={selectedOperator}
                                                        onChange={setSelectedOperator}
                                                    />
                                                </div>
                                                <div className="flex flex-col w-full">
                                                    <label htmlFor="value" className="mb-1">
                                                        Value
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="value"
                                                        min={0}
                                                        value={businessRuleValue}
                                                        onChange={(e) => setBusinessRuleValue(e.target.value)}
                                                        className="custom-input "
                                                    />
                                                </div>
                                                <div className="flex flex-col w-full">
                                                    <label htmlFor="business_rule_name" className="mb-1">
                                                        Business Rule Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="business_rule_name"
                                                        placeholder="CUST_BR_NAME"
                                                        value={businessRuleName}
                                                        onChange={(e) => setBusinessRuleName(e.target.value)}
                                                        className="custom-input "
                                                    />
                                                </div>
                                                <div className="flex w-full gap-2 justify-center mt-6">
                                                    <button
                                                        type="button"
                                                        className={`button-primary ${!isBusinessRuleAddEnabled
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : ""
                                                            }`}
                                                        disabled={!isBusinessRuleAddEnabled}
                                                        onClick={handleAddBusinessRule}
                                                    >
                                                        Add
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`button-red`}
                                                        onClick={handleClearAllBusinessRules}
                                                    >
                                                        Clear all
                                                    </button>
                                                </div>
                                            </div>
                                            {businessRules.length > 0 && (
                                                <div className="mt-7">
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full bg-white border border-gray-300 rounded">
                                                            <thead>
                                                                <tr className="bg-gray-100 font-normal">
                                                                    <th className="px-3 py-2 border-b text-left">
                                                                        Alias
                                                                    </th>
                                                                    <th className="px-3 py-2 border-b text-left">
                                                                        Operator
                                                                    </th>
                                                                    <th className="px-3 py-2 border-b text-left">
                                                                        Value
                                                                    </th>
                                                                    <th className="px-3 py-2 border-b text-left">
                                                                        Business Rule Name
                                                                    </th>
                                                                    <th className="px-3 py-2 border-b text-left">
                                                                        Actions
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {businessRules.map((rule, idx) => (
                                                                    <tr key={idx} className="border-b">
                                                                        {editIndex === idx ? (
                                                                            <>
                                                                                <td className="px-3 py-2 w-[210px]">
                                                                                    <Select
                                                                                        id={`edit-alias-${idx}`}
                                                                                        maxMenuHeight={80}
                                                                                        placeholder="Select Alias"
                                                                                        options={(() => {
                                                                                            const allAliases = [];

                                                                                            // Add DGS tag aliases
                                                                                            if (selectedValues.selectedDryGasSealTags?.tagAliasList) {
                                                                                                const dgsAliases = JSON.parse(
                                                                                                    selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"')
                                                                                                ).map((tag) => ({
                                                                                                    label: tag,
                                                                                                    value: tag,
                                                                                                }));
                                                                                                allAliases.push(...dgsAliases);
                                                                                            }

                                                                                            // Add independent tag aliases
                                                                                            if (selectedValues.selectedIndependentTags && Array.isArray(selectedValues.selectedIndependentTags)) {
                                                                                                const independentAliases = selectedValues.selectedIndependentTags.map((option) => ({
                                                                                                    label: option.tagAlias,
                                                                                                    value: option.tagAlias,
                                                                                                }));
                                                                                                allAliases.push(...independentAliases);
                                                                                            }

                                                                                            return allAliases;
                                                                                        })()}
                                                                                        value={editAlias}
                                                                                        isClearable
                                                                                        isSearchable
                                                                                        styles={customDropdownStyles}
                                                                                        onChange={setEditAlias}
                                                                                    />
                                                                                </td>
                                                                                <td className="px-3 py-2 w-[210px]">
                                                                                    <Select
                                                                                        id={`edit-operator-${idx}`}
                                                                                        maxMenuHeight={80}
                                                                                        placeholder="Select Operator"
                                                                                        options={[
                                                                                            {
                                                                                                label: "greater",
                                                                                                value: "greater",
                                                                                            },
                                                                                            { label: "lower", value: "lower" },
                                                                                            { label: "equal", value: "equal" },
                                                                                            {
                                                                                                label: "not equal",
                                                                                                value: "not equal",
                                                                                            },
                                                                                        ]}
                                                                                        isClearable
                                                                                        styles={customDropdownStyles}
                                                                                        value={editOperator}
                                                                                        onChange={setEditOperator}
                                                                                    />
                                                                                </td>
                                                                                <td className="w-[210px]">
                                                                                    <input
                                                                                        type="number"
                                                                                        min={0}
                                                                                        value={editValue}
                                                                                        onChange={(e) =>
                                                                                            setEditValue(e.target.value)
                                                                                        }
                                                                                        className="custom-input"
                                                                                    />
                                                                                </td>
                                                                                <td className="w-[210px]">
                                                                                    <input
                                                                                        type="text"
                                                                                        placeholder="CUST_BR_NAME"
                                                                                        value={editName}
                                                                                        onChange={(e) =>
                                                                                            setEditName(e.target.value)
                                                                                        }
                                                                                        className="custom-input"
                                                                                    />
                                                                                </td>
                                                                                <td className="px-3 py-2 flex gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`button-primary px-2 py-1 text-base ${!(
                                                                                            editAlias &&
                                                                                            editOperator &&
                                                                                            editValue !== "" &&
                                                                                            editName.trim() !== ""
                                                                                        )
                                                                                            ? "opacity-50 cursor-not-allowed"
                                                                                            : ""
                                                                                            }`}
                                                                                        disabled={
                                                                                            !(
                                                                                                editAlias &&
                                                                                                editOperator &&
                                                                                                editValue !== "" &&
                                                                                                editName.trim() !== ""
                                                                                            )
                                                                                        }
                                                                                        onClick={handleSaveEditBusinessRule}
                                                                                    >
                                                                                        Save
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="button-red px-2 py-1 text-base"
                                                                                        onClick={() =>
                                                                                            handleDeleteBusinessRule(null)
                                                                                        }
                                                                                    >
                                                                                        Delete
                                                                                    </button>
                                                                                </td>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <td className="px-3 py-2">
                                                                                    {rule.alias?.label || rule.alias?.value}
                                                                                </td>
                                                                                <td className="px-3 py-2">
                                                                                    {rule.operator?.label ||
                                                                                        rule.operator?.value}
                                                                                </td>
                                                                                <td className="px-3 py-2">
                                                                                    {rule.value}
                                                                                </td>
                                                                                <td className="px-3 py-2">{rule.name}</td>
                                                                                <td className="px-3 py-2 flex gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="button-primary px-2 py-1 text-base"
                                                                                        onClick={() =>
                                                                                            handleEditBusinessRule(idx)
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="button-red px-2 py-1 text-base"
                                                                                        onClick={() =>
                                                                                            handleDeleteBusinessRule(idx)
                                                                                        }
                                                                                    >
                                                                                        Delete
                                                                                    </button>
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        <button
                                                            onClick={handleApplyBusinessRules}
                                                            type="button"
                                                            className={`bg-btn-primary px-10 text-white p-1.5 my-5 rounded-full h-10`}
                                                        >
                                                            Apply
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        ""
                                    )}

                                <div
                                    className="flex justify-between items-center  hover:bg-gray-100 py-3 px-2 hover:cursor-pointer"
                                    onClick={toggleForecastSelection}
                                >
                                    <span className="text-btn-primary font-semibold flex items-center text-[16.5px]">
                                        Forecast Selection{" "}
                                    </span>
                                    {isForecastSelection ? (
                                        <IoIosArrowDown
                                            size={20}
                                            className="text-btn-primary cursor-pointer"
                                            onClick={toggleForecastSelection}
                                        />
                                    ) : (
                                        <IoIosArrowUp
                                            size={20}
                                            className="text-btn-primary cursor-pointer"
                                            onClick={toggleForecastSelection}
                                        />
                                    )}
                                </div>

                                {isForecastSelection ? (
                                    <>

                                        <div className='flex flex-col '>
                                            <div className="flex gap-4 mb-2">
                                                <div className="flex flex-col w-full">
                                                    <label htmlFor="rule-name" className="mb-1">
                                                        Tag Alias{" "}
                                                    </label>
                                                    <Select
                                                        id="forecast-tags"
                                                        placeholder="Select the Tags"
                                                        isClearable
                                                        isSearchable
                                                        isMulti
                                                        required
                                                        styles={customDropdownStyles}
                                                        options={getCombinedTagAliases(
                                                            selectedValues.selectedDryGasSealTags,
                                                            selectedValues.selectedIndependentTags
                                                        ).map((tag) => ({
                                                            label: tag,
                                                            value: tag,
                                                        }))}
                                                        value={forecastTagAlias}
                                                        onChange={setForecastTagAlias}
                                                    />
                                                </div>

                                                <div className="flex flex-col  w-full">
                                                    <label htmlFor="future-start-date" className="mb-1">
                                                        Future Start Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        id="future-start-date"
                                                        className="custom-input"
                                                        value={futureStartDateState}
                                                        onChange={(e) => setFutureStartDate(e.target.value)}
                                                    />
                                                </div>

                                                <div className="flex flex-col  w-full">
                                                    <label htmlFor="end-date" className="mb-1">
                                                        Future End Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        id="future-end-date"
                                                        className="custom-input "
                                                        value={futureEndDateState}
                                                        onChange={(e) => setFutureEndDate(e.target.value)}
                                                    />
                                                </div>

                                                <div className="flex flex-col w-full">
                                                    <label htmlFor="frequency" className="mb-1">
                                                        Frequency{" "}
                                                        {frequency !== "" ? <i> = {frequency} mins</i> : ""}{" "}
                                                    </label>
                                                    <div className="w-full flex items-end">
                                                        <input
                                                            type="number"
                                                            id="frequency"
                                                            placeholder="Enter frequency in mins"
                                                            value={frequency}
                                                            min={1}
                                                            onChange={(e) => {
                                                                const val = Number(e.target.value);
                                                                if (e.target.value === "" || val < 1) {
                                                                    setFrequency("");
                                                                } else {
                                                                    setFrequency(val);
                                                                }
                                                            }}
                                                            className="custom-input w-full"
                                                        />
                                                        <span className="text-gray-500 ml-2">mins</span>
                                                    </div>
                                                </div>


                                            </div>

                                            <div className="flex gap-4 ">
                                                <div className="flex flex-col mt-2  w-full">
                                                    <label htmlFor="name" className="mb-1">
                                                        Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="name"
                                                        placeholder="Enter name"
                                                        className="custom-input"
                                                        value={nameDesc.name}
                                                        onChange={(e) =>
                                                            setNameDesc((prev) => ({
                                                                ...prev,
                                                                name: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>

                                                <div className="flex flex-col mt-2  w-full">
                                                    <label htmlFor="description" className="mb-1">
                                                        Description (optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="description"
                                                        placeholder="Enter description"
                                                        className="custom-input"
                                                        value={nameDesc.desc}
                                                        onChange={(e) =>
                                                            setNameDesc((prev) => ({
                                                                ...prev,
                                                                desc: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                toast.promise(handleForecast(), {
                                                    loading: "Applying changes...",
                                                    success: <b>Forecast started!</b>,
                                                    error: <b>Failed to start the forecast.</b>,
                                                })
                                            }

                                            disabled={
                                                !(
                                                    selectedValues.selectedAssembly &&
                                                    getCombinedTagAliases(
                                                        selectedValues.selectedDryGasSealTags,
                                                        selectedValues.selectedIndependentTags
                                                    ).length > 0
                                                )
                                            }
                                            className={`button-primary mr-5 mt-8  ${!(
                                                selectedValues.selectedAssembly &&
                                                getCombinedTagAliases(
                                                    selectedValues.selectedDryGasSealTags,
                                                    selectedValues.selectedIndependentTags
                                                ).length > 0
                                            )
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                                }`}
                                        >
                                            {`Forecast`}

                                        </button>


                                    </>
                                ) : (
                                    ""
                                )}



                            </div>
                        </form>
                    </div>
                </FormCard>

                {liveForecastsData.length !== 0 &&
                    selectedValues.selectedAssembly &&
                    getCombinedTagAliases(
                        selectedValues.selectedDryGasSealTags,
                        selectedValues.selectedIndependentTags
                    ).length > 0 &&
                    <LiveForecastsTable
                        onViewForecast={handleViewForecastFromTable}
                        modelsData={liveForecastsData}
                        updateModelStatus={updateForecastModelStatus}
                        loading={loading}
                        setLoading={setLoading}
                        deleteModel={deleteForecastModel}
                        reloadModels={reloadForecastModels}
                    />
                }

                {selectedValues.selectedAssembly &&
                    getCombinedTagAliases(
                        selectedValues.selectedDryGasSealTags,
                        selectedValues.selectedIndependentTags
                    ).length > 0 &&
                    (chartLoading ? (

                        <p className="text-gray-600">Loading chart. Please wait...</p>

                    ) : (forecastTimeseriesData && forecastTimeseriesData.keys && forecastTimeseriesData.values) ? (
                        <TimeSeriesChart
                            chartData={forecastTimeseriesData}
                            colorTagOrder={selectedTagOrder}
                        />
                    ) : null)}

                {showForecastChart && forecastChartLoading && (
                    <p className="text-gray-600">Loading chart. Please wait...</p>
                )}

                {showForecastChart && !forecastChartLoading && forecastChartData && forecastChartData.tag_aliases && forecastChartData.tag_aliases.length !== 0 && (
                    <ForecastChart
                        forecastData={forecastChartData}
                        selectedAnomaly={selectedAnomaly}
                        tagOrder={forecastTagOrder}
                    />
                )}

                {showForecastChart && !forecastChartLoading && forecastChartData && forecastChartData.tag_aliases && forecastChartData.tag_aliases.length !== 0 && (
                    <AnomaliesTable
                        data={forecastChartData}
                        selectedTags={forecastTagAlias}
                        onRowSelect={setSelectedAnomaly}
                    />
                )}

            </div>

            {isNoForecastDataPopup && (
                <ConfirmationPopup
                    message={`No forecast data available for the selected model.`}
                    onConfirm={() => setIsNoForecastDataPopup(false)}
                    onCancel={() => setIsNoForecastDataPopup(false)}
                    cancelButtonMsg={""}
                    confirmButtonMsg={"OK"}
                />
            )}

            {isNoExistingModelsPopup && (
                <ConfirmationPopup
                    message={`No existing forecast models found.`}
                    onConfirm={() => setIsNoExistingModelsPopup(false)}
                    onCancel={() => setIsNoExistingModelsPopup(false)}
                    cancelButtonMsg={""}
                    confirmButtonMsg={"OK"}
                />
            )}

            {isNoModelsFoundPopup && (
                <ConfirmationPopup
                    message={`No forecast models found.`}
                    onConfirm={() => setIsNoModelsFoundPopup(false)}
                    onCancel={() => setIsNoModelsFoundPopup(false)}
                    cancelButtonMsg={""}
                    confirmButtonMsg={"OK"}
                />
            )}
        </>
    );
};

export default ForecastForm;